import { ChangeDetectorRef, Directive, inject, ViewChild } from "@angular/core";
import { Observable } from "rxjs";
import { ValidationError } from "bp-common-models-lib";
import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { CustomModal } from "@modules/core/components/base-modals";
import { InfoService } from "@core-services/info.service";
import { UserService } from "@main-services/user.service";
import { BpModalCanDismiss } from "@models/interfaces/bp-modal-can-dismiss";
import { Validateable } from "@models/interfaces/validateable";
import { Helper } from "@helpers/functions";
import { TWO_FA_ENABLING_STEPS } from "@helpers/constants";
import { User } from "@models/user";
import { ConfirmModalService } from "@core-services/confirm-modal.service";
import { NewProfileFacade } from "@modules/main/components/profile/new-profile/state/new-profile.facade";

@Directive()
export abstract class Security2FAModalComponent extends CustomModal implements BpModalCanDismiss {
	public readonly steps: number = TWO_FA_ENABLING_STEPS;
	public currentStep: number = 1;
	public secretKey: string; // This allows user to authenticate inside Authenticator App without QR code
	public QRdata: string; // Data used to generate the QR Code internally
	public smsCode: string;
	public passInput: string;
	public passwordError: Validateable;
	public authCodeError: Validateable;
	public recoveryInputValue: string;
	public recoveryInputError: string;
	public verifyCodeInputValue: string;
	public skipSmsPinStep: boolean = false;
	@ViewChild("smsPinInput") private smsPinComoponent;

	protected readonly cdr = inject(ChangeDetectorRef);

	public constructor(
		protected userService: UserService,
		protected apicallService: ApicallService,
		protected infoService: InfoService,
		protected profileFacade: NewProfileFacade,
		protected confirmModalService: ConfirmModalService
	) {
		super();
	}

	public modalCanDismiss(): boolean | Observable<boolean> {
		if (this.currentStep > 1 && this.currentStep !== this.steps) {
			// Not first step and not success step
			return this.confirmModalService.showConfirmModal("close_window?", "unsaved_data_will_get_lost");
		}
		return true;
	}

	public onClickContinue(): void {
		this.increaseStep();
	}

	public onClickBack(): void {
		this.decreaseStep();
	}

	protected increaseStep(): void {
		if (this.currentStep < this.steps) {
			this.currentStep++;
		}
	}

	protected decreaseStep(): void {
		if (this.currentStep > 1) {
			this.currentStep--;
		}
	}

	public setSmsCode(smsInput: string): void {
		this.smsCode = smsInput;
	}

	// Check if user's password is correct - API:
	// V2 endpoint will Request SMS
	public verifyUserPassword(useAuthCode: boolean = false): void {
		const argumentsObj = {
			password: this.passInput,
			auth_code: "",
			used_for: "activate"
		};

		// This endpoint uses auth_code for Disable, together with the password
		if (useAuthCode) {
			argumentsObj.auth_code = this.verifyCodeInputValue;
			argumentsObj.used_for = "deactivate";
		}

		const options = new ApicallOption(null, argumentsObj);
		this.apicallService.post("user/verify2FA", options).subscribe(
			(res) => {
				this.passInput = "";

				const attributes = res["attributes"];
				this.secretKey = attributes.secret;
				this.QRdata = decodeURIComponent(attributes.qr_url);

				if (useAuthCode) {
					this.user.setTwoFa(false);
				}

				// User has no sms code to check
				this.skipSmsPinStep = true;
				this.currentStep = 3;
				this.cdr.detectChanges();
			},
			(error) => {
				if (error instanceof ValidationError) {
					if (error.containsCode("twofa.already.enabled")) {
						this.infoService.setError(error);
					}
					if (error.metaContains("password")) {
						this.passwordError = new Validateable();
						this.passwordError.setValidationErrors(error);
					} else if (error.metaContains("auth_code")) {
						this.authCodeError = new Validateable();
						this.authCodeError.setValidationErrors(error);
					} else if (error.containsCode("2fa.de_activation.sms.verification.required")) {
						this.increaseStep();
						this.cdr.detectChanges();
					} else {
						this.infoService.setError(error);
					}
				} else {
					Helper.handleError(error);
				}
			}
		);
	}
	// Check if user's SMS code is correct - API:
	public verifySmsCode(disable2FA: boolean = false): void {
		const argumentsObj = {
			used_for: "activate",
			sms_pin_code: this.smsCode
		};

		if (disable2FA) {
			argumentsObj.used_for = "deactivate";
		}
		const url = "user/verify2FA";
		const a = new ApicallOption(null, argumentsObj);

		this.apicallService.post(url, a).subscribe(
			(res) => {
				const attributes = res["attributes"];
				this.secretKey = attributes.secret;
				this.QRdata = decodeURIComponent(attributes.qr_url);

				// Deactivate 2FA
				if (disable2FA) {
					this.user.setTwoFa(false);
					this.userService.userStatusUpdate(this.user);
				}
				this.increaseStep();
				this.cdr.detectChanges();
			},
			(e) => {
				Helper.handleError(e);
				this.smsPinComoponent.resetInput();
			}
		);
	}

	// 6-Digits PIN checked - API:

	protected pinCodeVerification(enable: boolean = true): void {
		const options = new ApicallOption(null, {
			auth_code: this.verifyCodeInputValue,
			auth_secret: this.secretKey
		});

		this.apicallService.post("user/activate2FA", options).subscribe(
			() => {
				this.user.setTwoFa(enable);
				if (enable) {
					this.user.setTwoFAEverActive(true);
				}

				this.recoveryInputValue = null;
				this.verifyCodeInputValue = null;
				this.secretKey = null;
				this.QRdata = null;
				this.increaseStep();
				this.cdr.detectChanges();

				this.userService.userStatusUpdate(this.user);
			},
			(error) => Helper.handleError(error)
		);
	}

	public get user(): User {
		return this.userService.user;
	}

	public onFinishedCodeInput(value: string): void {
		this.verifyCodeInputValue = value;
	}
}
